// Router code-based. Index `/` redireciona pro primeiro workspace; se não tem,
// abre WorkspaceShell com EmptyHero + modal de criar.

import {
  createRootRoute,
  createRoute,
  createRouter,
  isRedirect,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { api } from './lib/api.js';
import { RootShell } from './ui/RootShell.js';
import { LoginPage } from './ui/LoginPage.js';
import { RegisterPage } from './ui/RegisterPage.js';
import { BootstrapWizard } from './ui/BootstrapWizard.js';
import { WorkspaceShell } from './ui/WorkspaceShell.js';
import { DocumentEditorPage } from './ui/DocumentEditorPage.js';
import { ImporterPage } from './ui/ImporterPage.js';
import { ProfilePage } from './ui/ProfilePage.js';
import { BillingPage } from './ui/BillingPage.js';
import type { Workspace } from './lib/queries.js';

const rootRoute = createRootRoute({ component: RootShell });

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: async () => {
    try {
      const me = await api<{ id: string } | null>('/auth/me').catch(() => null);
      if (!me) throw redirect({ to: '/login' });
      const ws = await api<{ workspaces: Workspace[] }>('/workspaces');
      if (ws.workspaces.length === 0) {
        // Redireciona pra slug 'novo' (não existe) — WorkspaceShell mostra EmptyHero.
        throw redirect({ to: '/w/$slug', params: { slug: '__new__' } });
      }
      throw redirect({ to: '/w/$slug', params: { slug: ws.workspaces[0]!.slug } });
    } catch (err) {
      if (isRedirect(err)) throw err;
      throw redirect({ to: '/login' });
    }
  },
  component: () => null,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

const registerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/register',
  component: RegisterPage,
});

const bootstrapRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin/bootstrap',
  component: BootstrapWizard,
});

const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/profile',
  component: ProfilePage,
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/billing',
  component: BillingPage,
});

const workspaceShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/w/$slug',
  component: () => (
    <WorkspaceShell>
      <Outlet />
    </WorkspaceShell>
  ),
});

const workspaceIndexRoute = createRoute({
  getParentRoute: () => workspaceShellRoute,
  path: '/',
  component: () => null,
});

const documentRoute = createRoute({
  getParentRoute: () => workspaceShellRoute,
  path: '/d/$documentId',
  component: DocumentEditorPage,
});

const importerRoute = createRoute({
  getParentRoute: () => workspaceShellRoute,
  path: '/import',
  component: ImporterPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  registerRoute,
  bootstrapRoute,
  profileRoute,
  billingRoute,
  workspaceShellRoute.addChildren([workspaceIndexRoute, documentRoute, importerRoute]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
